import { createSelector } from 'reselect';

import { OFF } from '../../../constants/integrations';
import { detectUserTimezone, findTimezoneByText } from '../../../utils/date';
import { getSenderInfoObject } from '../../../utils/users';
import { IAppState } from '../../reducers';
import { selectUserEmail, selectUserInfo } from '../../selectors/auth';
import { selectUsers } from '../../selectors/users';
import { selectCsIsContentDigital } from './contents';
import { selectCreateSendMethod } from './method';

export const selectCsSendSettingsState = (state: IAppState) => state.createSend.sendSettings;

export const selectCsSobEnabled = createSelector(selectCsSendSettingsState, (settings) => settings.sobEnabled);

export const selectCsSobEmail = createSelector(selectCsSendSettingsState, (settings) => settings.sobEmail);

export const selectCsInternalNotes = createSelector(selectCsSendSettingsState, (settings) => settings.internalNotes);

export const selectCsBusinessReasonId = createSelector(
  selectCsSendSettingsState,
  (settings) => settings.businessReasonId,
);

export const selectCsOnHoldEnabled = createSelector(selectCsSendSettingsState, (settings) => settings.onHoldEnabled);

export const selectCsOnHoldUntil = createSelector(selectCsSendSettingsState, (settings) => settings.onHoldUntil);

export const selectIsCsOnHoldApplicable = createSelector(
  selectCreateSendMethod,
  selectCsIsContentDigital,
  (method, isContentDigital): boolean => method !== 'link' && !isContentDigital,
);

export const selectIsCsReleaseDateMissing = createSelector(
  selectIsCsOnHoldApplicable,
  selectCsOnHoldEnabled,
  selectCsOnHoldUntil,
  (isApplicable, isOnHold, onHoldUntil): boolean => isApplicable && isOnHold && !onHoldUntil.trim(),
);

export const selectCsClaimExpiration = createSelector(
  selectCsSendSettingsState,
  (settings) => settings.claimExpiration,
);

export const selectCsClaimExpirationTimezone = createSelector(selectCsSendSettingsState, (settings) =>
  findTimezoneByText(settings.claimExpirationTimezone),
);

export const selectCsClaimExpirationTimezoneText = createSelector(
  selectCsClaimExpirationTimezone,
  (timezone) => (timezone ?? detectUserTimezone()).text,
);

export const selectCsDisableSenderEmails = createSelector(
  selectCsSendSettingsState,
  (settings) => settings.disableSenderEmails,
);

export const selectCsDisableRecipientShippedEmail = createSelector(
  selectCsSendSettingsState,
  (settings) => settings.disableRecipientShippedEmail,
);

export const selectCsShippingOption = createSelector(selectCsSendSettingsState, (settings) => settings.shippingOption);

export const selectCsIsOfficeDropshipEnabled = createSelector(
  selectCsSendSettingsState,
  (settings) => settings.isOfficeDropshipEnabled,
);

export const selectCsIsSobActive = createSelector(selectUserEmail, selectCsSobEmail, (userEmail, sobEmail) => {
  if (!userEmail || !sobEmail) {
    return false;
  }

  return userEmail !== sobEmail;
});

export const selectCsSobUser = createSelector(
  selectUsers,
  selectCsSobEmail,
  selectCsIsSobActive,
  (users, sobEmail, isActive) => {
    if (!isActive || !sobEmail || !users) {
      return null;
    }

    return users.find(({ email }) => email === sobEmail);
  },
);

export const selectCsIsSobUserEmailIntegrationDisabled = createSelector(selectCsSobUser, (user) =>
  user ? user?.integration.email_integration === OFF : undefined,
);

export const selectCsUserSenderInformation = createSelector(selectUserInfo, (userInfo) =>
  getSenderInfoObject(userInfo?.sender),
);

export const selectCsSenderInformation = createSelector(
  selectCsUserSenderInformation,
  selectCsSobEmail,
  selectUsers,
  (userSenderInfo, sobEmail, users) => {
    if (sobEmail) {
      return users ? getSenderInfoObject(users.find(({ email }) => email === sobEmail)) : undefined;
    }

    return userSenderInfo;
  },
);
